import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';

import type { DispatchRejectionSuggestedSlot, DispatchRequestDto } from './types';

interface RejectDispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DispatchRequestDto | null;
  isPending: boolean;
  error?: unknown;
  onConfirm: (payload: {
    reason?: string;
    suggested_availability?: DispatchRejectionSuggestedSlot[];
    cc_emails?: string[];
  }) => void;
}

/**
 * Reason dialog for rejecting a dispatch request (差し戻し). Replaces the old
 * window.prompt — the reason is what the supplier reads to know how to retry
 * (e.g. 「当日は満載のため別日でお願いします」), so it deserves a real form
 * with context (field report 2026-07-31).
 */
export const RejectDispatchDialog = ({
  open,
  onOpenChange,
  request,
  isPending,
  error,
  onConfirm,
}: RejectDispatchDialogProps) => {
  const { t } = useTranslation('operations');
  const [reason, setReason] = useState('');
  const [slots, setSlots] = useState<DispatchRejectionSuggestedSlot[]>([]);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [ccEmails, setCcEmails] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setSlots([]);
      setReasonError(null);
      setCcEmails('');
    }
  }, [open]);

  const submit = () => {
    const trimmed = reason.trim();
    const hasIncompleteSlot = slots.some(
      (slot) => !slot.date.trim() || !slot.start_time.trim(),
    );
    if (hasIncompleteSlot) {
      setReasonError(
        t('efs.operations.dispatch.reject_dialog.incomplete_slot_error', {
          defaultValue: '追加した再配車可能日時は日付と開始時間を入力してください。',
        }),
      );
      return;
    }
    const duplicates = new Set(slots.map((slot) => `${slot.date}T${slot.start_time}`));
    if (slots.length !== duplicates.size) {
      setReasonError(
        t('efs.operations.dispatch.reject_dialog.duplicate_slot_error', {
          defaultValue: '同じ再配車可能日時が重複しています。',
        }),
      );
      return;
    }
    if (!trimmed) {
      setReasonError(
        t('efs.operations.dispatch.reject_dialog.feedback_required', {
          defaultValue: '差し戻し理由を入力してください。',
        }),
      );
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    const cc = ccEmails.split(',').map((email) => email.trim()).filter(Boolean);
    onConfirm({
      ...(trimmed ? { reason: trimmed } : {}),
      ...(slots.length ? { suggested_availability: slots } : {}),
      ...(cc.length ? { cc_emails: cc } : {}),
    });
  };

  const updateSlot = (index: number, patch: Partial<DispatchRejectionSuggestedSlot>) => {
    setSlots((current) =>
      current.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)),
    );
    setReasonError(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.dispatch.reject_dialog.title', {
              defaultValue: '配車依頼を差し戻す',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.dispatch.reject_dialog.subtitle', {
              defaultValue: '入力した理由は供給元にそのまま表示されます。',
            })}
          </DialogDescription>
        </DialogHeader>

        {request ? (
          <p className="text-sm text-efs-text-muted">
            {request.requested_date ?? '—'}
            {' · '}
            <span className="font-mono text-xs">{request.trip_code || '—'}</span>
          </p>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="reject-cc-emails">CC Email</Label>
          <Input id="reject-cc-emails" value={ccEmails} onChange={(event) => setCcEmails(event.target.value)} placeholder="多筆請以逗號分隔" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="dispatch-rejection-reason">
            {t('efs.operations.dispatch.reject_dialog.reason_label', {
              defaultValue: '差し戻し理由（必須）',
            })}
          </Label>
          <Textarea
            ref={reasonRef}
            id="dispatch-rejection-reason"
            rows={3}
            maxLength={2000}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setReasonError(null);
            }}
            placeholder={t('efs.operations.dispatch.reject_dialog.reason_placeholder', {
              defaultValue: 'ここに理由を入力してください',
            })}
            aria-invalid={!!reasonError}
            aria-describedby={reasonError ? 'dispatch-rejection-reason-error' : undefined}
            autoFocus
          />
          {reasonError ? (
            <p id="dispatch-rejection-reason-error" role="alert" className="text-sm text-red-900">
              {reasonError}
            </p>
          ) : (
            <p className="text-xs text-efs-text-muted">
              {t('efs.operations.dispatch.reject_dialog.reason_help', {
                defaultValue: '例：当日は入荷枠が満杯のため、別日への変更をお願いします',
              })}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label>
              {t('efs.operations.dispatch.reject_dialog.suggested_slots_label', {
                defaultValue: '再配車可能日時',
              })}
            </Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isPending || slots.length >= 5}
              onClick={() => setSlots((current) => [...current, { date: '', start_time: '' }])}
            >
              <Plus className="mr-1 h-3 w-3" />
              {t('efs.operations.dispatch.reject_dialog.add_slot', {
                defaultValue: '日時を追加',
              })}
            </Button>
          </div>
          {slots.length === 0 ? (
            <p className="text-xs text-efs-text-muted">
              {t('efs.operations.dispatch.reject_dialog.suggested_slots_help', {
                defaultValue: '必要に応じて、供給元が選べる日時を追加してください。',
              })}
            </p>
          ) : (
            <div className="space-y-2">
              {slots.map((slot, index) => (
                <div key={index} className="grid grid-cols-[minmax(0,1fr)_8rem_auto] gap-2">
                  <input
                    type="date"
                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={slot.date}
                    onChange={(event) => updateSlot(index, { date: event.target.value })}
                    aria-label={t('efs.operations.dispatch.reject_dialog.slot_date_label', {
                      defaultValue: '再配車可能日',
                    })}
                  />
                  <input
                    type="time"
                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={slot.start_time}
                    onChange={(event) => updateSlot(index, { start_time: event.target.value })}
                    aria-label={t('efs.operations.dispatch.reject_dialog.slot_time_label', {
                      defaultValue: '再配車可能時間',
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isPending}
                    onClick={() => setSlots((current) => current.filter((_, i) => i !== index))}
                    title={t('efs.operations.dispatch.reject_dialog.remove_slot', {
                      defaultValue: '日時を削除',
                    })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <LocalizedApiError error={error} showTechnicalDetails={false} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common' })}
          </Button>
          <Button type="button" variant="destructive" disabled={isPending} onClick={submit}>
            {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.operations.dispatch.reject_dialog.confirm', {
              defaultValue: '差し戻す',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
